"use client";
import { IResource, Office } from "@gettactic/api";
import { Stack } from "@gettactic/components/src/primitives/Stack";
import React, { useCallback, useState } from "react";
import { IoAdd as Plus } from "react-icons/io5";
import { CreateOrEditExternalIDs } from "@/features/integrations/components/external-ids/CreateOrEditExternalIDs";
import { IntegrationsExternalIdsTable } from "@/features/integrations/components/external-ids/IntegrationsExternalIdsTable";
import { useExternalsQuery } from "@/lib/hooks/useExternalsQuery";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@cmp-src/primitives/button";
import { EmptyView } from "@gettactic/components/src/data-display/EmptyView/EmptyView";

export interface IntegrationsExternalIdsPanelProps {
  office: Office;
  resources?: IResource[] | null;
}

export const IntegrationsExternalIdsPanel: React.FC<
  IntegrationsExternalIdsPanelProps
> = ({ office, resources }) => {
  const queryClient = useQueryClient();
  const externals = useExternalsQuery(resources?.[0]?.id || "", "resources");
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const invalidateQuery = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["useExternalsQuery"]
      }),
    [queryClient]
  );

  if (!resources) return null;

  const hasExternals = (externals.data || []).length > 0;

  return (
    <Stack>
      <CreateOrEditExternalIDs
        key={isCreateOpen ? "new" : "new-close"}
        isOpen={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        resources={resources}
        invalidateQuery={invalidateQuery}
      />

      <div className="flex items-center justify-between pb-2">
        <h3 className="font-medium text-2xl text-neutral-900 leading-6">
          External IDs
        </h3>
        {hasExternals && (
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            Add External ID
          </Button>
        )}
      </div>

      {hasExternals ? (
        <div className="mt-6">
          <IntegrationsExternalIdsTable
            data={externals.data || []}
            invalidateQuery={invalidateQuery}
          />
        </div>
      ) : (
        <div className="mt-6">
          <EmptyView
            title="No External IDs"
            message="Get started by clicking the 'Add External ID' button"
            actions={[
              {
                label: "Add External ID",
                onClick: () => setIsCreateOpen(true),
                icon: Plus
              }
            ]}
          />
        </div>
      )}
    </Stack>
  );
};
